<script lang="ts">
  import { onDestroy } from 'svelte';
  import QRCode from 'qrcode';
  import { Dialog, DialogContent, DialogTitle, DialogDescription } from '$lib/components/ui/dialog';
  import { type ImageType } from '@briefmarken_katalog/types';
  import { getUploadToken } from '$lib/services/philatelic_items';
  import { subscribeUploadPictureReady } from '$lib/services/sse';
  import { t } from '$lib/i18n';
  import { doLogin } from '$lib/stores/auth';

  let { open = $bindable(), onPictureReceived }: {
    open: boolean;
    onPictureReceived: (picture: ImageType) => void;
  } = $props();

  let qrDataUrl    = $state<string | undefined>(undefined);
  let sessionToken = $state<string | undefined>(undefined);
  let error        = $state<string | undefined>(undefined);
  let secondsLeft  = $state(0);

  let countdownInterval: ReturnType<typeof setInterval> | undefined;

  function clearCountdown() {
    if (countdownInterval !== undefined) {
      clearInterval(countdownInterval);
      countdownInterval = undefined;
    }
  }

  function startCountdown(totalSeconds: number) {
    clearCountdown();
    secondsLeft = totalSeconds;
    countdownInterval = setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft <= 0) clearCountdown();
    }, 1000);
  }

  async function init() {
    clearCountdown();
    qrDataUrl    = undefined;
    sessionToken = undefined;
    error        = undefined;
    secondsLeft  = 0;
    try {
      const { token } = await getUploadToken(doLogin);
      sessionToken = token;
      const uploadUrl = `${window.location.origin}/upload.html?token=${token}`;
      qrDataUrl = await QRCode.toDataURL(uploadUrl, { width: 256, margin: 2 });
      startCountdown(5 * 60);
    } catch (e: any) {
      error = e.message ?? 'Unknown error';
    }
  }

  const unsubscribe = subscribeUploadPictureReady((token, picture) => {
    if (token !== sessionToken) return;
    onPictureReceived(picture);
    open = false;
  });

  onDestroy(() => {
    clearCountdown();
    unsubscribe();
  });

  $effect(() => {
    if (open) init();
    else clearCountdown();
  });
</script>

<Dialog bind:open>
  <DialogContent>
    <DialogTitle>{$t('upload.title')}</DialogTitle>
    <DialogDescription>{$t('upload.description')}</DialogDescription>
    <div class="flex flex-col items-center gap-4 py-4">
      {#if error}
        <p class="text-sm text-red-500">{error}</p>
      {:else if qrDataUrl}
        <img src={qrDataUrl} alt="QR Code" class="rounded transition-[filter] duration-500"
             class:blur-md={secondsLeft <= 0} />
        <p class="text-sm text-muted-foreground">{$t('upload.waiting')}</p>
        {#if secondsLeft > 0}
          <p class="text-sm text-muted-foreground">
            {$t('upload.validFor').replace('{s}', String(secondsLeft))}
          </p>
        {:else}
          <p class="text-sm text-destructive">{$t('upload.expired')}</p>
        {/if}
      {:else}
        <p class="text-sm text-muted-foreground">{$t('upload.generating')}</p>
      {/if}
    </div>
  </DialogContent>
</Dialog>
